import { useState, useMemo } from "react";
import { Plus, Minus, MessageCircle, Calculator } from "lucide-react";
import { useStorePicker } from "../hooks/useStorePicker";

// Simplified items grouped by category (subset of full pricing for quick estimation)
const ITEMS = [
  { id: "shirt", label: "Shirt / T-Shirt (Steam Iron)", price: 15, cat: "iron" },
  { id: "trouser", label: "Trouser / Jeans (Steam Iron)", price: 15, cat: "iron" },
  { id: "kurta", label: "Kurta (Steam Iron)", price: 30, cat: "iron" },
  { id: "saree", label: "Saree (Dry Clean)", price: 179, cat: "dc" },
  { id: "suit2", label: "Men's Suit 2-pc (Dry Clean)", price: 269, cat: "dc" },
  { id: "sherwani", label: "Sherwani (Dry Clean)", price: 349, cat: "dc" },
  { id: "lehenga", label: "Lehenga (Dry Clean)", price: 399, cat: "dc" },
  { id: "wash_kg", label: "Wash & Fold (per kg)", price: 60, cat: "kg" },
  { id: "wash_iron_kg", label: "Wash & Steam Iron (per kg)", price: 100, cat: "kg" },
  { id: "premium_kg", label: "Premium Laundry (per kg)", price: 160, cat: "kg" },
  { id: "sneakers", label: "Sports Shoes / Sneakers", price: 249, cat: "shoe" },
  { id: "leather_shoe", label: "Leather Shoes", price: 299, cat: "shoe" },
];

export const PriceEstimator = () => {
  const [qty, setQty] = useState({});
  const { open } = useStorePicker();

  const total = useMemo(
    () => ITEMS.reduce((sum, it) => sum + (qty[it.id] || 0) * it.price, 0),
    [qty]
  );
  const totalItems = useMemo(
    () => ITEMS.reduce((sum, it) => sum + (qty[it.id] || 0), 0),
    [qty]
  );
  const discounted = Math.round(total * 0.8);
  const savings = total - discounted;

  const setQ = (id, delta) => setQty((q) => ({ ...q, [id]: Math.max(0, (q[id] || 0) + delta) }));

  return (
    <section id="estimator" className="py-16 lg:py-20 bg-gradient-to-br from-sky-50/50 via-white to-lime-50/40 relative overflow-hidden">
      <div className="absolute top-10 left-10 w-3 h-3 bg-sky-300/60 rounded-full animate-float pointer-events-none" />
      <div className="absolute bottom-16 right-16 w-2.5 h-2.5 bg-lime-300/60 rounded-full animate-float pointer-events-none" style={{animationDelay:"1s"}} />

      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-600 tracking-wider uppercase mb-4">
            <Calculator className="w-4 h-4" /> Instant Estimate
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
            What will your order cost?
          </h2>
          <p className="text-slate-500 leading-relaxed">
            Pick your items and quantities — see your total update instantly. No account, no signup.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Items */}
          <div className="lg:col-span-2 bg-white rounded-3xl border border-sky-100 p-5 sm:p-6 shadow-sm">
            <div className="grid sm:grid-cols-2 gap-3">
              {ITEMS.map((it) => {
                const q = qty[it.id] || 0;
                return (
                  <div
                    key={it.id}
                    className={`flex items-center justify-between gap-3 p-3 rounded-xl border transition-colors ${
                      q > 0 ? "border-sky-300 bg-sky-50/40" : "border-slate-200"
                    }`}
                    data-testid={`estimator-item-${it.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{it.label}</p>
                      <p className="text-xs text-slate-500">₹{it.price}{it.cat === "kg" ? "/kg" : ""}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setQ(it.id, -1)}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center border transition-colors ${
                          q > 0 ? "border-sky-300 hover:bg-sky-100" : "border-slate-200 text-slate-300"
                        }`}
                        aria-label={`Decrease ${it.label}`}
                        disabled={q === 0}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-6 text-center text-sm font-bold text-slate-900" data-testid={`estimator-qty-${it.id}`}>{q}</span>
                      <button
                        type="button"
                        onClick={() => setQ(it.id, 1)}
                        className="w-7 h-7 rounded-lg bg-sky-600 hover:bg-sky-700 text-white flex items-center justify-center transition-colors"
                        aria-label={`Increase ${it.label}`}
                        data-testid={`estimator-plus-${it.id}`}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Total */}
          <div className="bg-slate-900 text-white rounded-3xl p-6 shadow-xl shadow-slate-900/10 h-fit lg:sticky lg:top-24">
            <p className="text-sm text-sky-200 uppercase tracking-wider font-semibold mb-1">Your Estimate</p>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-5xl font-extrabold tracking-tight" data-testid="estimator-total">
                ₹{total.toLocaleString("en-IN")}
              </span>
            </div>
            <p className="text-xs text-slate-400 mb-6">{totalItems} item{totalItems === 1 ? "" : "s"} · Free pickup & delivery</p>

            {total > 0 && (
              <div className="bg-lime-500/10 border border-lime-400/30 rounded-2xl p-4 mb-5">
                <p className="text-xs text-lime-300 font-semibold uppercase tracking-wider mb-1">With BLITZ20 code</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-lime-300">₹{discounted.toLocaleString("en-IN")}</span>
                  <span className="text-xs text-lime-200/70">You save ₹{savings}</span>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => open("whatsapp")}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-lime-500 hover:bg-lime-400 text-slate-900 font-bold text-sm transition-colors shadow-lg shadow-lime-500/20"
              data-testid="estimator-book-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Book on WhatsApp
            </button>
            <p className="text-[10px] text-slate-500 text-center mt-3">
              Final price may vary based on actual weight & fabric type.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
